import { GetServerSideProps, GetServerSidePropsContext } from "next/types";
import { useEffect, useState } from "react";

import { getAnalytics } from "@tm/insurance/analytics";
import { navigationActions } from "@tm/insurance/navigation";
import {
  deepMergeObjects,
  getCookieByName,
  getDeviceIdForServer,
  getRandomBgImage,
  getThemePartner,
  replaceHistory,
} from "@tm/insurance/utils";
import { PARTNER } from "@tm/isomorphic-utils";
import { getCommonsHeaderData } from "@tm/server-api-utils";
import { getPartnerOnServerSide } from "@tm/server-utils";
import {
  InsuranceCommonLandingPageMainWrapper,
  InsuranceGiCommonLandingPageMainWrapper,
} from "@tm/static/pages";
import { PartnerContextProvider } from "@tm/static/partnerContext";
import { withDeviceAndThemeProvider } from "@tm/ui-widgets";
import {
  withExperimentTrackingStringProvider,
  withReferrerProvider,
} from "@tm/ui-widgets/hoc";
import { withError } from "@tm/ui/hoc";
import { initGlobalErrorLogging } from "@tm/utility/analytics";
import dynamic from "next/dynamic";
import React from "react";
import { conditionallyLoadWebVitals } from "../../src/utils/webVitals";

import { SEO_PAGES_QUERY_ID, SEO_PAGES_V2_QUERY_ID } from "@tm/api-utils";
import {
  getSeoLandingPageDetailsByCountry,
  getSeoLandingPageDetailsByCountryForPwa,
} from "@tm/server-api-utils";
import { useRouter } from "next/router";
import { withSeoHeadTags } from "../../src/hoc";
import {
  authenticatedGSSP,
  AuthenticatedGSSPProps,
} from "../../src/utils/authenticatedGSSP";

const analytics = getAnalytics();

// Conditional dynamic imports with SSR enabled - only load what's needed based on device
// Using webpack magic comments for better code splitting
const InsuranceLanding = dynamic(
  () =>
    import(
      /* webpackChunkName: "insurance-landing-mobile" */
      "@tm/static/pages"
    ).then((mod) => mod.InsuranceLanding),
  { ssr: true }
);

const InsuranceCommonLanding = dynamic(
  () =>
    import(
      /* webpackChunkName: "insurance-common-landing-desktop" */
      "@tm/static/pages"
    ).then((mod) => mod.InsuranceCommonLanding),
  { ssr: true }
);

// Lazy load header HOC modules for code splitting
// Create wrapper components that use the HOCs internally
// This enables proper code splitting while maintaining SSR
const GIHeaderWrapper = dynamic(
  () =>
    import(
      /* webpackChunkName: "gi-insurance-header" */
      "@tm/ui/hoc"
    ).then((mod) => {
      const { withGIInsuranceHeader } = mod;
      return function GIHeaderWrapperComponent({
        children,
        headerOptions,
        mobileTitle,
        myOrders,
        goToMyOrders,
        displayWhiteHeader,
        showPwaHeaderBackButton,
        onLoginSuccess,
      }: {
        children: React.ReactNode;
        headerOptions?: any;
        mobileTitle?: string;
        myOrders?: boolean;
        goToMyOrders?: () => void;
        displayWhiteHeader?: boolean;
        showPwaHeaderBackButton?: boolean;
        onLoginSuccess?: () => void;
      }) {
        return withGIInsuranceHeader(
          <>{children}</>,
          headerOptions,
          mobileTitle,
          myOrders,
          goToMyOrders,
          displayWhiteHeader,
          showPwaHeaderBackButton,
          onLoginSuccess
        );
      };
    }),
  { ssr: true }
);

const MMTHeaderWrapper = dynamic(
  () =>
    import(
      /* webpackChunkName: "mmt-insurance-header" */
      "@tm/ui/hoc"
    ).then((mod) => {
      const { withMMTInsuranceHeader } = mod;
      return function MMTHeaderWrapperComponent({
        children,
        headerOptions,
        mobileTitle,
        myOrders,
        goToMyOrders,
        displayWhiteHeader,
        isMyBiz,
        exitWebViewOnBack,
        showPwaHeaderBackButton,
        showPwaHeader,
      }: {
        children: React.ReactNode;
        headerOptions?: any;
        mobileTitle?: string;
        myOrders?: boolean;
        goToMyOrders?: () => void;
        displayWhiteHeader?: boolean;
        isMyBiz?: boolean;
        exitWebViewOnBack?: boolean;
        showPwaHeaderBackButton?: boolean;
        showPwaHeader?: boolean;
      }) {
        return withMMTInsuranceHeader(
          <>{children}</>,
          headerOptions,
          mobileTitle,
          myOrders,
          goToMyOrders,
          displayWhiteHeader,
          isMyBiz,
          exitWebViewOnBack,
          showPwaHeaderBackButton,
          showPwaHeader
        );
      };
    }),
  { ssr: true }
);
interface CountryPageProps {
  country: string;
}

const getLandingPageJsonForPwaCountry = async (country, partner) => {
  try {
    const response = await getSeoLandingPageDetailsByCountryForPwa(
      country,
      partner
    );
    const defaultJSON = response?.find(
      (json) =>
        json?.type ===
        (partner === PARTNER.GI
          ? SEO_PAGES_QUERY_ID.LANDING_PAGE_COUNTRY_GI
          : SEO_PAGES_QUERY_ID.LANDING_PAGE_COUNTRY)
    );
    const countryJSON = response?.find(
      (json) =>
        json?.type ===
        `seo-insurance${
          partner === PARTNER.GI ? `-${partner}` : ""
        }-landing-${country}`
    );

    const countryName = countryJSON?.data?.travelCountry;

    const mergedJSON = deepMergeObjects(defaultJSON, countryJSON);

    const jsonWithCountry = JSON.stringify(mergedJSON)?.replace(
      /{travelCountry}/g,
      countryName
    );
    return jsonWithCountry ? JSON.parse(jsonWithCountry) : null;
  } catch (error) {
    console.error({ error });
    throw error;
  }
};

const getLandingPageJsonByCountry = async (country, partner) => {
  try {
    const response = await getSeoLandingPageDetailsByCountry(country, partner);

    const defaultJSON = response?.find(
      (json) =>
        json?.type ===
        (partner === PARTNER.GI
          ? SEO_PAGES_V2_QUERY_ID.LANDING_PAGE_COUNTRY_GI
          : SEO_PAGES_V2_QUERY_ID.LANDING_PAGE_COUNTRY)
    );

    const countryJSON = response?.find(
      (json) =>
        json?.type ===
        `seo-insurance${
          partner === PARTNER.GI ? `-${partner}` : ""
        }-landing-${country}`
    );

    const countryName = countryJSON?.data?.travelCountry;

    const mergedJSON = deepMergeObjects(defaultJSON, countryJSON);

    const jsonWithCountry = JSON.stringify(mergedJSON)?.replace(
      /{travelCountry}/g,
      countryName
    );

    const parsed = jsonWithCountry ? JSON.parse(jsonWithCountry) : null;

    // Wrap the data property in another data object
    return parsed ? { ...parsed, data: { data: parsed.data } } : null;
  } catch (error) {
    console.error({ error });
    throw error;
  }
};

export function CommonLandingPage({
  data,
  device = "mobile",
  error,
  // hasPartnerAuth,
  partner,
  referrer,
  cmp = "",
  experiment = "",
  query,
  commonsHeaderData,
  country,
  isTrackCookies,
  bgImageIndex,
  isMobileDevice,
}: AuthenticatedGSSPProps) {
  const headTags = data?.headTags || {};
  const longImageUrl = data?.offers?.items?.[0]?.image_url;
  const [randomImage, setRandomImage] = useState<string | number>(
    getRandomBgImage(bgImageIndex)
  );

  const router = useRouter();
  const countryParam = (router?.query?.country as string) || country;

  useEffect(() => {
    const cleanup = initGlobalErrorLogging(
      `seo_country_landing_${countryParam || "unknown"}`
    );
    return cleanup;
  }, [countryParam]);

  useEffect(() => {
    conditionallyLoadWebVitals();
  }, []);

  useEffect(() => {
    if (partner === PARTNER.MMT) {
      document.cookie = `isCommonLanding=true; domain=.makemytrip.com; path=/`;
    }
    const referrer = document.referrer;
    let hostname = referrer ? new URL(referrer).hostname : "";
    const url = new URL(window.location.href);
    if (!hostname) {
      hostname = url.hostname;
    }

    let updatedCmp = cmp;
    if (
      updatedCmp === "" &&
      (hostname === "www.makemytrip.com" || hostname === "www.goibibo.com")
    ) {
      updatedCmp = "secondary_icon";
      replaceHistory("cmp", updatedCmp);
    }
    if (updatedCmp === "" && hostname === "www.google.com") {
      updatedCmp = "seo";
      replaceHistory("cmp", updatedCmp);
    }
    sessionStorage.setItem("cmp", updatedCmp);

    // const bgImage = getRandomBgImage();
    // const commonLandingMainWrapper = document.getElementById(
    //   "common-landing-main-wrapper"
    // );
    // if (commonLandingMainWrapper && bgImage) {
    //   commonLandingMainWrapper.style.backgroundImage = `url(${bgImage})`;
    // }
    // Set random image on client side only
    const bgImage = getRandomBgImage(bgImageIndex);
    setRandomImage(bgImage);
  }, [bgImageIndex, cmp, partner]);

  const [activeModal, setActiveModal] = useState("");

  const goToMyOrders = () => {
    analytics?.trackLandingEvents(
      {
        actionType: "my_orders_clicked",
      }
      //'click'
    );

    const authCookie =
      getCookieByName("mmt-auth") || getCookieByName("OAUTH-GOIBIBO");

    if (authCookie) {
      navigationActions.navigateToOrderListing();
    } else {
      setActiveModal?.("LOGIN");
    }
  };
  const onModalChange = (modal: string) => {
    if (modal === "LOGIN" || modal === "") {
      setActiveModal(modal);
    }
  };
  //TODO: Create a global context and move common data things there
  const comp =
    partner === PARTNER.MYPARTNER ||
    partner === PARTNER.MYBIZ ? null : partner === PARTNER.GI ? (
      <PartnerContextProvider partnerType={PARTNER.GI}>
        <GIHeaderWrapper
          headerOptions={{
            Wrapper: (props: any) => (
              <InsuranceGiCommonLandingPageMainWrapper
                id="common-landing-main-wrapper"
                {...props}
              />
            ),
            forceLogin: false,
          }}
          mobileTitle="Secure International Travel"
          myOrders={true}
          goToMyOrders={goToMyOrders}
        >
          {isMobileDevice ? (
            <InsuranceLanding
              setActiveModal={setActiveModal}
              data={data}
              query={query}
            />
          ) : (
            <InsuranceCommonLanding
              setActiveModal={setActiveModal}
              data={data}
              query={query}
              partnerType={partner}
            />
          )}
        </GIHeaderWrapper>
      </PartnerContextProvider>
    ) : (
      <PartnerContextProvider partnerType={PARTNER.MMT}>
        <MMTHeaderWrapper
          headerOptions={{
            type: "dualHeader",
            Wrapper: (props: any) => (
              <InsuranceCommonLandingPageMainWrapper
                style={{
                  backgroundImage: `url(${randomImage}), linear-gradient(to bottom, #051322, #15457c)`,
                  backgroundSize: "100% 643px",
                }}
                id="common-landing-main-wrapper"
                {...props}
              >
                {/* Hidden img tag to make LCP image discoverable in HTML with fetchpriority */}
                {typeof randomImage === "string" && !isMobileDevice && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={randomImage}
                    alt=""
                    style={{
                      position: "absolute",
                      width: 0,
                      height: 0,
                      opacity: 0,
                      pointerEvents: "none",
                    }}
                    {...({ fetchpriority: "high" } as any)}
                    loading="eager"
                  />
                )}
                {props.children}
              </InsuranceCommonLandingPageMainWrapper>
            ),
            activeModal: activeModal || "",
            onModalChange: onModalChange,
            forceLogin: false,
            pokusExperiments: {
              insuranceNewFunnel: true,
            },
            globalConfig: commonsHeaderData?.headerConfig?.value,
            headerCommonData: {
              loyaltyHeaderData: commonsHeaderData?.loyaltyHeaderData?.value,
              walletHeaderData: commonsHeaderData?.walletHeaderData?.value,
              mobileCore: commonsHeaderData?.mobileCore?.value,
              userInfoData: commonsHeaderData?.userInfoData?.value,
            },
          }}
          mobileTitle="Insurance & Protection Plans"
          myOrders={true}
          goToMyOrders={goToMyOrders}
          displayWhiteHeader={false}
          isMyBiz={false}
          exitWebViewOnBack={false}
          showPwaHeaderBackButton={true}
          showPwaHeader={isMobileDevice}
        >
          {isMobileDevice ? (
            <InsuranceLanding
              setActiveModal={setActiveModal}
              data={data}
              query={query}
            />
          ) : (
            <InsuranceCommonLanding
              setActiveModal={setActiveModal}
              data={data}
              query={query}
            />
          )}
        </MMTHeaderWrapper>
      </PartnerContextProvider>
    );

  return withError(
    withReferrerProvider(
      withDeviceAndThemeProvider(
        withSeoHeadTags(
          withExperimentTrackingStringProvider(comp, experiment),
          headTags,
          partner,
          longImageUrl
        ),
        device,
        { pageTheme: getThemePartner(partner) }
      ),
      referrer
    ),
    error,
    "COMMON_LANDING"
  );
}

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const { req, query, res } = ctx;

  const partner = getPartnerOnServerSide(req?.headers);
  const dvid = getDeviceIdForServer(req, res, partner);

  const { cmp = "" } = query || {};
  const requestData = { partner, headers: req.headers, cmp, query, dvid };

  let commonsHeaderData = null;
  if (partner !== PARTNER.GI) {
    const profile =
      partner === PARTNER.MYBIZ
        ? "BUSINESS"
        : partner === PARTNER.MYPARTNER
        ? "CTA"
        : "PERSONAL";
    commonsHeaderData = await getCommonsHeaderData(
      dvid,
      profile,
      req?.cookies?.["mmt-auth"]
    );
  }

  return authenticatedGSSP(
    ctx,
    ({ isMobileDevice }) => {
      if (isMobileDevice) {
        return getLandingPageJsonForPwaCountry(query?.country ?? "", partner);
      }
      return getLandingPageJsonByCountry(query?.country ?? "", partner);
    },
    // getLandingPageJsonByCountry(query?.country ?? "", partner),
    partner,
    cmp,
    "common-landing",
    commonsHeaderData
  );
};

export default CommonLandingPage;
